import { COUNTRY_CURRENCY_MAP, COUNTRY_LANGUAGE_MAP } from '@/config/regional'

/**
 * Get user's preferred language
 * Priority: auth store > localStorage > browser locale > country-based > default
 * @param {Object} authStore - Optional auth store instance
 * @returns {string} 2-letter language code (e.g., 'en', 'es')
 */
export function getUserLanguage(authStore = null) {
  // 1. Check authenticated user's profile
  if (authStore?.isAuthenticated && authStore?.user?.language) {
    console.log(`🌍 Using user profile language: ${authStore.user.language}`)
    return authStore.user.language
  }

  // 2. Check localStorage
  const savedLanguage = localStorage.getItem('language')
  if (savedLanguage) {
    return savedLanguage
  }

  // 3. Try browser locale
  try {
    const locale = navigator.language || navigator.userLanguage
    if (locale) {
      const lang = locale.split('-')[0].toLowerCase()
      console.log(`🌍 Using browser language: ${lang}`)
      return lang
    }
  } catch (e) {
    console.warn('Browser language detection failed:', e)
  }

  // 4. Try country-based default
  const country = getUserCountry(authStore)
  if (COUNTRY_LANGUAGE_MAP[country]) {
    console.log(`🌍 Using country-default language for ${country}: ${COUNTRY_LANGUAGE_MAP[country]}`)
    return COUNTRY_LANGUAGE_MAP[country]
  }

  return 'en'
}

/**
 * Get the DETECTED country (geo-IP or cached) - ignores profile
 * Use this for location-based features like Local Trending
 * @returns {string} 2-letter country code
 */
export function getDetectedCountry() {
  // 1. Check cached geo-detection result
  const cachedCountry = localStorage.getItem('detected_country')
  if (cachedCountry) {
    return cachedCountry
  }

  // 2. Try browser locale detection
  try {
    const locale = navigator.language || navigator.userLanguage
    if (locale && locale.includes('-')) {
      const country = locale.split('-')[1]?.toUpperCase()
      if (country && country.length === 2) {
        return country
      }
    }
  } catch (e) {
    console.warn('Browser locale detection failed:', e)
  }

  // 3. Default fallback
  return 'AU'
}

/**
 * Get the DETECTED city (geo-IP or cached)
 * Use this for default origin city in searches
 * @returns {string} City name
 */
export function getDetectedCity() {
  return localStorage.getItem('detected_city') || 'Sydney'
}

/**
 * Get user's country code for location-based features (e.g. Local Trending).
 *
 * Priority:
 *   1. Real-time IP detection (cached in localStorage by detectLocationFromIP)
 *   2. Authenticated user's profile country — fallback when IP detection fails
 *      (covers: VPN/proxy returning wrong country, localhost dev, network error)
 *   3. Browser locale region (e.g. en-AU → AU, es-CO → CO)
 *   4. Final default: 'AU'
 *
 * @param {Object} authStore - Optional auth store instance
 * @returns {string} 2-letter country code (e.g., 'AU', 'US', 'CO')
 */
export function getUserCountry(authStore = null) {
  // 1. Real-time geo-IP (written by detectLocationFromIP on app mount)
  const cachedCountry = localStorage.getItem('detected_country')
  if (cachedCountry) {
    console.log(`🌍 Using IP-detected country: ${cachedCountry}`)
    return cachedCountry
  }

  // 2. Profile country — fallback when IP detection fails or has no result
  if (authStore?.isAuthenticated && authStore?.user?.country) {
    console.log(`🌍 IP detection unavailable — using profile country: ${authStore.user.country}`)
    return authStore.user.country
  }

  // 3. Browser locale (e.g. en-AU → AU, es-CO → CO)
  try {
    const locale = navigator.language || navigator.userLanguage
    if (locale && locale.includes('-')) {
      const country = locale.split('-')[1]?.toUpperCase()
      if (country && country.length === 2) {
        console.log(`🌍 Using browser locale country: ${country}`)
        return country
      }
    }
  } catch (e) {
    console.warn('Browser locale detection failed:', e)
  }

  // 4. Final fallback
  return 'AU'
}

/**
 * Get user's preferred currency
 * Priority: explicit selection > auth store > localStorage > country-based > default
 * @param {string} countryCode - 2-letter ISO country code
 * @param {Object} authStore - Optional auth store instance (for authenticated users)
 * @returns {string} Currency code (e.g., 'AUD', 'USD', 'EUR')
 */
export function getCurrencyForCountry(countryCode, authStore = null) {
  // 1. Check if user has EXPLICITLY selected a currency (footer selector)
  // This takes precedence over everything, including profile
  const hasExplicitSelection = localStorage.getItem('currency_user_selected') === 'true'
  const savedCurrency = localStorage.getItem('currency')

  if (hasExplicitSelection && savedCurrency) {
    console.log(`💱 Using explicit user selection: ${savedCurrency}`)
    return savedCurrency
  }

  // 2. Check authenticated user's profile
  // ALWAYS prioritize profile over geo-IP cache if user is signed in
  if (authStore?.isAuthenticated && authStore?.user?.currency) {
    console.log(`💱 Using user profile currency: ${authStore.user.currency}`)
    return authStore.user.currency
  }

  // 3. Check localStorage (non-explicit, from geo-detection)
  // Only use this if not signed in or profile has no currency
  if (savedCurrency) {
    return savedCurrency
  }

  // 4. Map country to currency
  const currency = COUNTRY_CURRENCY_MAP[countryCode] || 'USD'
  return currency
}

/**
 * Async geo-detection using ipapi.co
 * Call this on app initialization for unauthenticated users
 * Results are cached in localStorage
 * @param {boolean} forceRefresh - If true, re-detect even if cached
 * @returns {Promise<{country: string, currency: string}>}
 */
export async function detectLocationFromIP(forceRefresh = false) {
  // Check if we already have cached results (unless forcing refresh)
  const cachedCountry = localStorage.getItem('detected_country')
  if (!forceRefresh && cachedCountry) {
    const currency = localStorage.getItem('currency') || COUNTRY_CURRENCY_MAP[cachedCountry] || 'USD'
    console.log(`🌍 Using cached geo-detection: ${cachedCountry}, ${currency}`)
    return { country: cachedCountry, currency }
  }

  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000)
    let response
    try {
      response = await fetch('https://ipapi.co/json/', { signal: controller.signal })
    } finally {
      clearTimeout(timeoutId)
    }
    if (!response.ok) throw new Error('ipapi request failed')

    const data = await response.json()
    const country = data.country_code?.toUpperCase() || 'AU'
    const city = data.city || 'Sydney' // Capture city
    const detectedCurrency = data.currency || COUNTRY_CURRENCY_MAP[country] || 'USD'

    // Cache the location data
    localStorage.setItem('detected_country', country)
    localStorage.setItem('detected_city', city) // Store city

    // Check if user has explicitly selected a currency
    const hasExplicitCurrencyPreference = localStorage.getItem('currency_user_selected') === 'true'
    const savedCurrency = localStorage.getItem('currency')

    if (!savedCurrency || !hasExplicitCurrencyPreference) {
      localStorage.setItem('currency', detectedCurrency)
      window.dispatchEvent(new CustomEvent('currency-changed', { detail: detectedCurrency }))
    }

    console.log(`🌍 Geo-detected location: ${city}, ${country}, currency: ${detectedCurrency}`)
    return { country, city, currency: detectedCurrency }
  } catch (error) {
    console.warn('IP geo-detection failed:', error)

    // Persist fallback values so subsequent getDetectedCountry/getUserCountry
    // calls return AU/Sydney/AUD instead of falling through to browser locale
    const fallbackCountry = 'AU'
    const fallbackCity = 'Sydney'
    const fallbackCurrency = 'AUD'

    try {
      localStorage.setItem('detected_country', fallbackCountry)
      localStorage.setItem('detected_city', fallbackCity)

      // Only set currency if user hasn't explicitly chosen one
      const hasExplicitCurrencyPreference = localStorage.getItem('currency_user_selected') === 'true'
      if (!hasExplicitCurrencyPreference) {
        localStorage.setItem('currency', fallbackCurrency)
        window.dispatchEvent(new CustomEvent('currency-changed', { detail: fallbackCurrency }))
      }
    } catch {
      // Storage write failure — non-critical, fallback values still returned
    }

    return { country: fallbackCountry, city: fallbackCity, currency: fallbackCurrency }
  }
}

/**
 * Save user's explicit currency preference
 * This prevents auto-detection from overwriting user's choice
 * @param {string} currency - Currency code
 */
export function saveUserCurrencyPreference(currency) {
  localStorage.setItem('currency', currency)
  localStorage.setItem('currency_user_selected', 'true')
  console.log(`💱 User explicitly selected currency: ${currency}`)
}

/**
 * Clear cached geo-detection data
 * Call this when user logs out or wants to re-detect location
 */
export function clearLocationCache() {
  localStorage.removeItem('detected_country')
  localStorage.removeItem('detected_city')
  localStorage.removeItem('currency')
  localStorage.removeItem('currency_user_selected')
  localStorage.removeItem('language')
  console.log('🌍 Location cache cleared')
}
